<h2 mat-dialog-title>Edit Profile</h2>
<mat-dialog-content>
    <form [formGroup]="profileForm">
        <div class="form-container">
            <div class="form-section">
                <h3>Personal Information</h3>
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Title</mat-label>
                    <mat-select formControlName="title" required>
                        <mat-option *ngFor="let title of titles" [value]="title.value">
                            {{ title.text }}
                        </mat-option>
                    </mat-select>
                    <mat-error *ngIf="profileForm.get('title')?.touched && profileForm.get('title')?.invalid">
                        Please enter a title
                    </mat-error>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Name</mat-label>
                    <input matInput type="text" formControlName="name" required>
                    <mat-error *ngIf="profileForm.get('name')?.invalid">Please enter a name</mat-error>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Surname</mat-label>
                    <input matInput type="text" formControlName="surname" required>
                    <mat-error *ngIf="profileForm.get('surname')?.invalid">Please enter a surname</mat-error>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Country</mat-label>
                    <mat-select formControlName="country" required>
                        <mat-option *ngFor="let country of countries" [value]="country.value">
                            {{ country.text }}
                        </mat-option>
                    </mat-select>
                    <mat-error *ngIf="profileForm.get('country')?.touched && profileForm.get('country')?.invalid">
                        Please enter a country
                    </mat-error>
                </mat-form-field>
            </div>

            <div class="form-section" *ngIf="profileForm.get('userType')?.value === 'doctor'">
                <h3>Professional Information</h3>
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Speciality</mat-label>
                    <mat-select formControlName="speciality" required>
                        <mat-option *ngFor="let speciality of specialities" [value]="speciality.value">
                            {{ speciality.text }}
                        </mat-option>
                    </mat-select>
                    <mat-error *ngIf="profileForm.get('speciality')?.touched && profileForm.get('speciality')?.invalid">
                        Please enter a speciality
                    </mat-error>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>HPCSA Number</mat-label>
                    <input matInput type="text" formControlName="mpNumber" required>
                    <mat-error *ngIf="profileForm.get('mpNumber')?.invalid">Please enter your HPCSA number</mat-error>
                    <mat-icon matSuffix matTooltip="Please contact us to change your HPCSA number">info</mat-icon>
                </mat-form-field>

                <!-- What forms ask of the practitioner; CURA fills these in from here. -->
                <mat-form-field appearance="outline" class="full-width"
                    *ngIf="profileForm.get('userType')?.value === 'doctor'">
                    <mat-label>Qualifications</mat-label>
                    <input matInput type="text" formControlName="qualifications" placeholder="MBChB (UCT), FCS(SA) Neurosurgery">
                    <mat-hint>As you would write them on a medical report</mat-hint>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full-width"
                    *ngIf="profileForm.get('userType')?.value === 'doctor'">
                    <mat-label>Year of first qualifying</mat-label>
                    <input matInput type="number" formControlName="yearQualified" min="1950" max="2100">
                </mat-form-field>
                <mat-form-field appearance="outline" class="full-width"
                    *ngIf="profileForm.get('userType')?.value === 'doctor'">
                    <mat-label>ID number</mat-label>
                    <input matInput type="text" formControlName="idNumber" maxlength="13">
                    <mat-hint>Asked for on insurer and Compensation Fund reports; stored encrypted</mat-hint>
                </mat-form-field>

                <mat-form-field appearance="outline" class="full-width"
                    *ngIf="profileForm.get('userType')?.value === 'doctor'">
                    <mat-label>Lancet Ordering Code</mat-label>
                    <input matInput type="text" formControlName="lancetCode">
                </mat-form-field>
            </div>

            <!--
                Lancet result delivery. Doctor-only, and self-only: the server refuses both of
                these fields for any user other than the caller, so they exist here and nowhere
                else. Do not copy this block into the lab-order config dialog, which is where a
                practice manager fills in a colleague's Lancet ordering code.
            -->
            <div class="form-section lancet-delivery" *ngIf="showLancetDeliverySection">
                <h3>Lancet Result Delivery</h3>

                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Send unmatched results to</mat-label>
                    <mat-select formControlName="lancetDeliveryGroupId">
                        <mat-option [value]="null">No preference - deliver to all my practices</mat-option>
                        <mat-option *ngFor="let group of userGroups" [value]="group.id">
                            {{ group.name }}
                        </mat-option>
                        <mat-option *ngIf="savedDeliveryGroupMissing" [value]="savedDeliveryGroupId">
                            A practice you are no longer a member of
                        </mat-option>
                    </mat-select>
                </mat-form-field>
                <p class="mat-caption hint">
                    When a Lancet result arrives for your HPCSA number and we cannot match it to
                    one of your patients, send it to this practice only, instead of to every
                    practice you work at.
                </p>
                <p class="mat-caption hint">
                    This does not change where matched results go. A result we can match to one of
                    your patients is always delivered to that patient's practice, whatever you
                    choose here.
                </p>
                <p class="mat-caption hint lancet-delivery-warning" *ngIf="savedDeliveryGroupMissing">
                    <mat-icon inline>warning</mat-icon>
                    The practice currently saved here is one you are no longer an active member of,
                    so it is being ignored and unmatched results are going to all your practices.
                    Please choose again.
                </p>

                <ng-container *ngIf="showLancetDeliveryToggle">
                    <!--
                        Stated BEFORE the switch moves, not in a toast afterwards. The switch
                        itself is intercepted: turning it on puts it straight back to off and
                        opens the confirmation below.
                    -->
                    <p class="mat-caption hint m-t-10 lancet-delivery-warning">
                        <mat-icon inline>info</mat-icon>
                        Turning this on turns it OFF on any other MedicAI account registered under
                        the same HPCSA number - only one account can receive your Lancet
                        results. If this is your only account, it changes nothing.
                    </p>
                    <div class="m-b-10">
                        <mat-slide-toggle formControlName="lancetDeliveryEnabled" color="primary"
                            (change)="onLancetDeliveryEnabledChange($event)">
                            Deliver results for my HPCSA number to this account
                        </mat-slide-toggle>
                    </div>

                    <div class="lancet-delivery-confirm" *ngIf="showLancetDeliveryConfirm">
                        <p class="mat-body-strong">Claim Lancet delivery for this account?</p>
                        <p class="mat-caption">
                            Results sent to your HPCSA number will be delivered here. If you hold
                            another MedicAI account under the same HPCSA number, delivery will be
                            switched off there when you save, and it will stop receiving them.
                        </p>
                        <div class="lancet-delivery-confirm-actions">
                            <button type="button" mat-button (click)="cancelLancetDeliveryEnabled()">Cancel</button>
                            <button type="button" mat-flat-button color="primary"
                                (click)="confirmLancetDeliveryEnabled()">Yes, deliver here</button>
                        </div>
                    </div>
                </ng-container>
            </div>

            <div class="form-section">
                <h3>Account Information</h3>
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Email</mat-label>
                    <input matInput type="email" formControlName="email" required>
                    <mat-error *ngIf="profileForm.get('email')?.invalid">Please enter a valid email</mat-error>
                </mat-form-field>
            </div>


            <div class="form-section">
                <h3>Default Settings</h3>
                <div class="m-b-10" *ngIf="profileForm.get('userType')?.value !== 'doctor'">
                    <mat-slide-toggle formControlName="ccMeInCommunication" color="primary">CC me in communication sent from my account</mat-slide-toggle>
                    <p class="mat-caption hint">When on, your email will be included as CC on patient invites, consent requests, and similar emails sent from your account.</p>
                </div>
                <div class="m-b-10" *ngIf="profileForm.get('userType')?.value === 'doctor'">
                    <mat-slide-toggle formControlName="suppressConsent" color="primary">Disable File Alert
                        Popups</mat-slide-toggle>
                </div>
                <div class="m-b-10" *ngIf="profileForm.get('userType')?.value === 'doctor' && userGroups.length > 1">
                    <mat-slide-toggle formControlName="calendarAllPractices" color="primary">Show appointments from all my practices</mat-slide-toggle>
                    <p class="mat-caption hint">When on, the calendar shows appointments from every practice you belong to, not just the one selected.</p>
                </div>
                <!-- <div class="m-b-10">
                    <mat-slide-toggle formControlName="disableAutoDownload" color="primary">Disable Auto Recording
                        Download</mat-slide-toggle>
                </div> -->
                <mat-form-field appearance="outline" class="full-width m-t-10">
                    <mat-label>Preferred Theme</mat-label>
                    <mat-select formControlName="preferredTheme" required (selectionChange)="previewTheme($event)">
                        <mat-option value="darkMode">Dark Theme</mat-option>
                        <mat-option value="lightMode">Light Theme</mat-option>
                    </mat-select>
                </mat-form-field>
            </div>

            <div class="form-section" *ngIf="profileForm.get('userType')?.value === 'doctor'">
                <h3 class="m-t-10">Physician Signature</h3>

                <div class="signature-preview" *ngIf="signatureImageUrl">
                    <img [src]="signatureImageUrl" alt="Physician Signature" />
                    <button mat-icon-button color="warn" (click)="clearSignature()">
                        <mat-icon>delete</mat-icon>
                    </button>
                </div>


                <div class="signature-pad-container m-t-5" *ngIf="!signatureImageUrl">
                    <div class="signature-pad">
                        <app-signature-pad [emitOnDragEnd]="true" format="base64" (done)="getSignatureData($event)"
                            [hideDetails]="true">
                        </app-signature-pad>
                    </div>
                </div>
            </div>
        </div>
    </form>

    <!--
        The save failed and the dialog is still open on purpose. The two Lancet delivery controls
        have already been put back to what the server holds, so nothing on screen is claiming a
        value that was refused.
    -->
    <div class="save-error" role="alert" *ngIf="saveError">
        <mat-icon inline>error_outline</mat-icon>
        <span>{{ saveError }}</span>
    </div>
</mat-dialog-content>
<mat-dialog-actions align="end">
    <!-- Disabled while the save is in flight, and the label changes too: the only other progress
         indicator is a 4px bar at the top of the viewport, which users miss. -->
    <button (click)="onSubmit()" mat-raised-button color="primary" [disabled]="isSaving">
        <mat-icon>save</mat-icon>{{ isSaving ? 'Saving…' : 'Save Changes' }}
    </button>
</mat-dialog-actions>